import { useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Bookmark,
  CalendarCheck,
  Check,
  Columns3,
  ExternalLink,
  Link2,
  MessageSquare,
  Minus,
  Share2,
} from "lucide-react";
import { toast } from "sonner";

import { glanceRows, type OrgStoryView } from "@/lib/orgStory";
import type { FitReasons } from "@/lib/fitReasons";
import { httpUrl } from "@/lib/publicOrganizationProfile";
import { LISTED_FIT_LABEL } from "@/lib/listedOrganization";
import { trackInteraction } from "@/lib/analytics";
import { useAgentOrgActions } from "@/hooks/useAgentOrgActions";
import { Button } from "@/components/ui/button";
import FitScoreRing, { fitTierLabel } from "@/components/ui/FitScoreRing";
import SurfaceCard from "@/components/primitives/SurfaceCard";
import { cn } from "@/lib/utils";
import AtAGlanceCard from "./AtAGlanceCard";

/**
 * The story's decision panel (spec 2026-09-24 §5.2): fit, the conversation,
 * Save / Compare, a discovery call and "who we're looking for", kept in view
 * while the story scrolls. MobileDecisionBar is its slim bottom-bar form.
 * Spec 2026-10-01 adds "Why this fit" under the ring and the At a glance card.
 */

export interface StoryFit {
  /** Whether this viewer may see fit at all (the page's canShowFitScore). */
  show: boolean;
  loading: boolean;
  score: number | null;
  /**
   * There is a score, but it rests on too few answered dimensions to show as a
   * number (`src/lib/fitEvidence.ts`): `score` is null and the panel says "Not
   * enough information to score". The reasons, which are words, still show.
   */
  thin?: boolean;
  /** Why this fit (spec 2026-10-01 §5). Absent or null shows the ring alone. */
  reasons?: FitReasons | null;
}

interface DecisionPanelProps {
  view: OrgStoryView;
  fit: StoryFit;
  /** Undefined hides Message (members, signed-out). Reuses the page's conversation flow. */
  onMessage?: () => void;
  /** Undefined hides the discovery call (no link, or calls switched off). */
  onBookCall?: () => void;
  /** The page's LookingForCard, or null. */
  lookingFor: ReactNode;
  /** Save / Compare apply to agents outside the org. */
  showOrgActions?: boolean;
  /** An unpublished story seen by its own team. */
  draft?: boolean;
  settingsHref?: string;
  /** Opens the onboarding wizard in place; when absent, "Set preferences" links to settingsHref. */
  onSetPreferences?: () => void;
  /** "public": the signed-out panel (sign-in links, no org actions). Never calls an auth hook. */
  variant?: "signedIn" | "public";
  /** False drops the primary action — used by the public page's mobile in-flow panel, where the bottom bar carries it instead (the signed-in page drops Message by omitting `onMessage`). */
  showPrimary?: boolean;
  /** Where the public variant's links go; defaults to PUBLIC_SIGN_IN_HREF. */
  signInHref?: string;
  /** False drops the Share link from the footer (the story hero carries Share). */
  showShare?: boolean;
  /** Public variant: open the page's sign-in box instead of linking to signInHref. */
  onSignIn?: () => void;
  /** Public variant: open the page's registration form from the fit block. */
  onRegister?: () => void;
  /**
   * Public variant: what "Start a conversation" opens. The public page passes
   * sign-up with the message prompt (founder decision F6); without it the
   * button falls back to onSignIn.
   */
  onStartConversation?: () => void;
  /** False drops the Website link from the footer (the story hero carries it). */
  showWebsite?: boolean;
  /** False drops the At a glance card. */
  showGlance?: boolean;
}

/** A viewer who can't see fit — used by the public page (signed-in members get a computed fit with `show: false`). */
export const NO_FIT: StoryFit = { show: false, loading: false, score: null, reasons: null };

/** Where a signed-out visitor goes to sign in or join — the same target the public page's LookingForCard uses. */
export const PUBLIC_SIGN_IN_HREF = "/agents";

/**
 * At-a-glance rows a signed-out visitor does not see (founder decision F6):
 * the compensation model, fees and leads sit behind a sign-up prompt on the
 * public page.
 */
const SIGNED_OUT_HIDDEN_GLANCE_ROWS = new Set(["model", "fees", "leads"]);

function FitBlock({
  fit,
  settingsHref,
  onSetPreferences,
}: {
  fit: StoryFit;
  settingsHref: string;
  onSetPreferences?: () => void;
}) {
  if (!fit.show) return null;

  const thin = !fit.loading && fit.thin === true;
  const unscored = !fit.loading && fit.score == null && !thin;
  const reasons = !fit.loading && (fit.score != null || thin) ? fit.reasons : null;
  const hasReasons = !!reasons && (reasons.matches.length > 0 || !!reasons.gap);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        {fit.loading ? (
          <div className="h-12 w-12 shrink-0 animate-pulse rounded-full bg-muted" aria-hidden />
        ) : (
          <FitScoreRing score={thin ? null : fit.score} size={48} />
        )}
        <div className="min-w-0">
          <p className="eyebrow text-[10px] text-ink-500">Your fit</p>
          <p className="text-sm font-semibold text-ink-900" data-testid={thin ? "fit-not-enough-info" : undefined}>
            {fit.loading ? "Scoring…" : thin ? LISTED_FIT_LABEL : fitTierLabel(fit.score)}
          </p>
          {unscored &&
            (onSetPreferences ? (
              <button
                type="button"
                onClick={onSetPreferences}
                className="rounded-md text-left text-[13px] text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Set preferences to see your fit
              </button>
            ) : (
              <Link to={settingsHref} className="text-[13px] text-primary hover:underline">
                Set preferences to see your fit
              </Link>
            ))}
        </div>
      </div>

      {hasReasons && (
        <ul aria-label="Why this fit" className="flex flex-col gap-1.5 text-[13px]">
          {reasons.matches.map((label) => (
            <li key={label} className="flex items-center gap-2 text-ink-900">
              <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
              {label}
            </li>
          ))}
          {reasons.gap && (
            <li className="flex items-center gap-2 text-ink-500">
              <Minus className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                <span className="sr-only">Less aligned: </span>
                {reasons.gap}
              </span>
            </li>
          )}
        </ul>
      )}

      {unscored && (
        <p className="text-[12px] leading-snug text-ink-500">
          Scored on compensation model, training, leads, benefits and culture.
        </p>
      )}
    </div>
  );
}

function PublicFitBlock({
  name,
  signInHref,
  onSignIn,
  onRegister,
}: {
  name: string;
  signInHref: string;
  onSignIn?: () => void;
  onRegister?: () => void;
}) {
  if (onSignIn && onRegister) {
    return (
      <div className="flex items-center gap-3">
        <div aria-hidden>
          <FitScoreRing score={null} size={48} />
        </div>
        <div className="min-w-0">
          <p className="eyebrow text-[10px] text-ink-500">Your fit</p>
          <button
            type="button"
            onClick={onRegister}
            className="rounded-md text-left text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Register as an agent
          </button>
          <p className="text-[13px] text-ink-500">
            to see your fit score with {name}. Already registered?{" "}
            <button
              type="button"
              onClick={onSignIn}
              className="rounded-sm font-medium text-ink-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <div aria-hidden>
        <FitScoreRing score={null} size={48} />
      </div>
      <div className="min-w-0">
        <p className="eyebrow text-[10px] text-ink-500">Your fit</p>
        {onSignIn ? (
          <button
            type="button"
            onClick={onSignIn}
            className="rounded-md text-left text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            See how you fit
          </button>
        ) : (
          <Link to={signInHref} className="text-sm font-semibold text-primary hover:underline">
            See how you fit
          </Link>
        )}
        <p className="text-[13px] text-ink-500">
          {onSignIn ? `Score ${name} against what you want.` : `Sign in to score ${name} against what you want.`}
        </p>
      </div>
    </div>
  );
}

/** Copies the page URL. Reads window/navigator only inside the handler (prerender-safe). */
async function shareStoryLink() {
  try {
    await navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied");
  } catch {
    toast.error("Couldn't copy the link");
  }
}

/**
 * The story hero's buttons: Save / Compare for an agent outside the org, and
 * Share for everyone. The public variant never renders OrgActionButtons, so it
 * never calls useAgentOrgActions.
 */
export function StoryHeroActions({
  view,
  showOrgActions = true,
  variant = "signedIn",
}: {
  view: OrgStoryView;
  showOrgActions?: boolean;
  variant?: "signedIn" | "public";
}) {
  return (
    <>
      {variant === "signedIn" && showOrgActions && <OrgActionButtons view={view} inline />}
      <Button variant="outline" size="icon" onClick={shareStoryLink} aria-label="Copy link to this page">
        <Share2 className="h-4 w-4" />
      </Button>
    </>
  );
}

/** Save / Compare. Its own component so only the signed-in panel ever calls useAgentOrgActions. */
function OrgActionButtons({ view, inline = false }: { view: OrgStoryView; inline?: boolean }) {
  const { orgId, name } = view.identity;
  const { compareIds, toggleCompare, savedOrgIds, toggleSave } = useAgentOrgActions();
  const saved = savedOrgIds.includes(orgId);
  const comparing = compareIds.includes(orgId);

  const onSave = () => {
    if (!orgId) return;
    const next = !saved;
    toggleSave(orgId, name);
    void trackInteraction(orgId, next ? "save" : "unsave");
  };

  return (
    <div className={inline ? "contents" : "grid grid-cols-2 gap-2"}>
      <Button variant="outline" onClick={onSave} aria-pressed={saved}>
        <Bookmark className={cn("h-4 w-4", saved && "fill-primary text-primary")} />
        {saved ? "Saved" : "Save"}
      </Button>
      <Button variant="outline" onClick={() => toggleCompare(orgId, name)} aria-pressed={comparing}>
        {comparing ? <Check className="h-4 w-4 text-success" /> : <Columns3 className="h-4 w-4" />}
        {comparing ? "Comparing" : "Compare"}
      </Button>
    </div>
  );
}

export function DecisionPanel({
  view,
  fit,
  onMessage,
  onBookCall,
  lookingFor,
  showOrgActions = true,
  draft = false,
  settingsHref = "/settings",
  onSetPreferences,
  variant = "signedIn",
  showPrimary = true,
  signInHref = PUBLIC_SIGN_IN_HREF,
  showShare = true,
  onSignIn,
  onRegister,
  onStartConversation,
  showWebsite = true,
  showGlance = true,
}: DecisionPanelProps) {
  const startConversation = onStartConversation ?? onSignIn;
  const { name, website } = view.identity;
  const site = showWebsite ? httpUrl(website) : null;
  const isPublic = variant === "public";

  return (
    <div className="flex flex-col gap-4">
      <SurfaceCard padding="lg" className="flex flex-col gap-4 rounded-2xl">
        {draft && (
          <p className="rounded-lg border border-border bg-muted px-3 py-2 text-[13px] text-ink-700">
            Draft — only your team can see this.
          </p>
        )}

        {isPublic ? (
          <>
            <PublicFitBlock name={name} signInHref={signInHref} onSignIn={onSignIn} onRegister={onRegister} />
            {showPrimary && startConversation && (
              <Button className="w-full" onClick={startConversation}>
                <MessageSquare className="h-4 w-4" />
                Start a conversation
              </Button>
            )}
            {showPrimary && !startConversation && (
              <Button asChild className="w-full">
                <Link to={signInHref}>
                  <MessageSquare className="h-4 w-4" />
                  Sign in to message
                </Link>
              </Button>
            )}
          </>
        ) : (
          <>
            <FitBlock fit={fit} settingsHref={settingsHref} onSetPreferences={onSetPreferences} />
            {showPrimary && onMessage && (
              <Button className="w-full" onClick={onMessage} aria-label={`Start a conversation with ${name}`}>
                <MessageSquare className="h-4 w-4 shrink-0" />
                Start a conversation
              </Button>
            )}
            {showOrgActions && <OrgActionButtons view={view} />}
          </>
        )}

        {onBookCall && (
          <Button variant="outline" className="w-full" onClick={onBookCall}>
            <CalendarCheck className="h-4 w-4" />
            Book a discovery call
          </Button>
        )}
      </SurfaceCard>

      {showGlance && (
        <AtAGlanceCard
          rows={isPublic ? glanceRows(view).filter((row) => !SIGNED_OUT_HIDDEN_GLANCE_ROWS.has(row.key)) : glanceRows(view)}
          sections={view.sections}
        />
      )}

      {lookingFor}

      {(showShare || site) && (
      <div className="flex items-center gap-4 px-1 text-[13px] text-ink-500">
        {showShare && (
        <button
          type="button"
          onClick={shareStoryLink}
          className="inline-flex items-center gap-1.5 rounded-md hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Link2 className="h-3.5 w-3.5" />
          Share
        </button>
        )}
        {site && (
          <a
            href={site}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Website
          </a>
        )}
      </div>
      )}
    </div>
  );
}

interface MobileDecisionBarProps {
  view: OrgStoryView;
  fit: StoryFit;
  onMessage?: () => void;
  onBookCall?: () => void;
  /** The public bar: a sign-in link that takes precedence over onMessage/onBookCall. */
  signInHref?: string;
  /** The public bar: opens the page's sign-in box; takes precedence over signInHref. */
  onSignIn?: () => void;
  /** The public bar: what "Start a conversation" opens; takes precedence over onSignIn. */
  onStartConversation?: () => void;
}

/** The CSS var CompareTray reads to lift itself above this bar (see BOTTOM_BAR_HEIGHT_VAR). */
const BOTTOM_BAR_HEIGHT_VAR = "--story-bottom-bar-h";
/** Fallback height (matches the bar's typical rendered height) when ResizeObserver is unavailable. */
const FALLBACK_BAR_HEIGHT_PX = 64;

/**
 * Below lg: a slim fixed bar with the fit chip and one primary action (spec
 * §5.3). Renders as a fragment — an `lg:hidden` spacer of the same height,
 * then the fixed bar itself — so the page needs no extra bottom padding of
 * its own to keep content from sliding under the bar.
 *
 * While mounted and visible (below the `lg` breakpoint), it also publishes
 * its rendered height on `document.documentElement` as the
 * `--story-bottom-bar-h` CSS variable, which `CompareTray` reads to lift
 * itself above the bar on phones. The variable is removed whenever the bar
 * is hidden (at `lg`+, or on unmount) so CompareTray falls back to its
 * ordinary desktop offset.
 */
export function MobileDecisionBar({
  view,
  fit,
  onMessage,
  onBookCall,
  signInHref,
  onSignIn,
  onStartConversation,
}: MobileDecisionBarProps) {
  const startConversation = onStartConversation ?? onSignIn;
  const action = onMessage
    ? { label: "Start a conversation", icon: MessageSquare, run: onMessage }
    : onBookCall
      ? { label: "Book a call", icon: CalendarCheck, run: onBookCall }
      : null;

  const visible = !!action || fit.show || !!signInHref || !!startConversation;
  const barRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = document.documentElement;

    if (!visible) {
      root.style.removeProperty(BOTTOM_BAR_HEIGHT_VAR);
      return;
    }

    const bar = barRef.current;
    if (!bar) return;

    // Some test environments (jsdom) don't implement matchMedia at all; treat
    // that as "not desktop" rather than throwing, same spirit as the optional
    // chaining SectionPills uses for its own matchMedia call.
    const mql = typeof window.matchMedia === "function" ? window.matchMedia("(min-width: 1024px)") : null;

    const updateHeight = () => {
      if (mql?.matches) {
        // At lg+ the bar is hidden (lg:hidden); publish no offset for it.
        root.style.removeProperty(BOTTOM_BAR_HEIGHT_VAR);
        return;
      }
      root.style.setProperty(BOTTOM_BAR_HEIGHT_VAR, `${bar.getBoundingClientRect().height}px`);
    };

    updateHeight();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateHeight);
      observer.observe(bar);
    } else if (!mql?.matches) {
      // No ResizeObserver in this environment: publish a fixed fallback once.
      root.style.setProperty(BOTTOM_BAR_HEIGHT_VAR, `${FALLBACK_BAR_HEIGHT_PX}px`);
    }

    mql?.addEventListener("change", updateHeight);

    return () => {
      observer?.disconnect();
      mql?.removeEventListener("change", updateHeight);
      root.style.removeProperty(BOTTOM_BAR_HEIGHT_VAR);
    };
  }, [visible]);

  if (!visible) return null;

  const Icon = action?.icon;

  return (
    <>
      {/* Reserves the bar's height in normal flow so page content never sits under it. */}
      <div aria-hidden className="h-[calc(4.5rem+env(safe-area-inset-bottom))] lg:hidden" />
      <div
        ref={barRef}
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-card/85 lg:hidden"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto flex max-w-[1140px] items-center justify-between gap-3">
          {fit.show ? (
            <div className="flex min-w-0 items-center gap-2">
              {fit.loading ? (
                <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-muted" aria-hidden />
              ) : (
                <FitScoreRing score={fit.thin ? null : fit.score} size={32} />
              )}
              <span className="truncate text-sm font-medium text-ink-900">
                {fit.loading ? "Scoring…" : fit.thin ? LISTED_FIT_LABEL : fitTierLabel(fit.score)}
              </span>
            </div>
          ) : (
            <span className="truncate text-sm font-medium text-ink-900">{view.identity.name}</span>
          )}

          {startConversation ? (
            <Button onClick={startConversation} className="shrink-0">
              <MessageSquare className="h-4 w-4" />
              Start a conversation
            </Button>
          ) : signInHref ? (
            <Button asChild className="shrink-0">
              <Link to={signInHref}>
                <MessageSquare className="h-4 w-4" />
                Sign in to message
              </Link>
            </Button>
          ) : (
            action &&
            Icon && (
              <Button
                onClick={action.run}
                className="shrink-0"
                aria-label={action.label === "Message" ? `Message ${view.identity.name}` : undefined}
              >
                <Icon className="h-4 w-4" />
                {action.label}
              </Button>
            )
          )}
        </div>
      </div>
    </>
  );
}
